import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import { IMemberState } from '@/store/types'
import { ResponseStatus, MemberAPI, MemberEntity } from '@/api/member'

import {
  MemberMatchHistoryPaginatedResponse,
  MemberMatchHistoryEntity,
  MatchGameType,
  MatchAPI
} from '@/api/match'

import {
  MemberTournamentHistoryPaginatedResponse,
  MemberTournamentHistoryItemEntity,
  TournamentGameType,
  TournamentAPI
} from '@/api/tournament'

import store from '@/store'
import { AccountInfo, IdentityAPI } from '@/api/identity'

@Module({ dynamic: true, store: store, namespaced: true, name: 'member' })
export default class MemberModule extends VuexModule implements IMemberState {
  // the active profile id
  memberId: string = ''
  member: MemberEntity | null = null

  matchHistory: MemberMatchHistoryEntity[] = []
  matchHistoryCurrentPage: number = 0
  matchHistoryHasMorePage: boolean = true

  tournamentHistory: MemberTournamentHistoryItemEntity[] = []
  tournamentHistoryCurrentPage: number = 0
  tournamentHistoryHasMorePage: boolean = true

  isPhoneVerificationVisible: boolean = false
  isDiscordIdDialogVisible: boolean = false

  // Selected member
  @Mutation
  SET_SELECTED_MEMBER(member: MemberEntity | null) {
    if (member === null) {
      this.memberId = ''
      this.member = null
    } else {
      this.memberId = member!.memberId
      this.member = member
    }
  }

  @Action({ rawError: true })
  async setSelectedMember(memberId: string) {
    try {
      const response = await MemberAPI.getMember(memberId)
      let payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_SELECTED_MEMBER', payload)
      }
    } catch {}
  }

  @Action
  resetSelectedMember() {
    this.context.commit('SET_SELECTED_MEMBER', null)
  }

  // Member Match History
  @Action({ rawError: true })
  async fetchMemberMatchHistory(params: { page: number; game: MatchGameType }) {
    let page = params.page
    let game = params.game
    if (!this.memberId) return
    if (page <= this.matchHistoryCurrentPage || !this.matchHistoryHasMorePage) {
      return
    }
    this.context.commit('SET_MATCH_HISTORY_CURRENT_PAGE', page)
    try {
      const response = await MatchAPI.getMemberMatchHistory(
        this.memberId,
        page,
        game
      )
      let payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_MATCH_HISTORY', payload)
      }
    } catch {}
  }

  @Action
  resetMemberMatchHistory() {
    this.context.commit('RESET_MATCH_HISTORY')
  }

  @Mutation
  SET_MATCH_HISTORY_CURRENT_PAGE(page: number) {
    this.matchHistoryCurrentPage = page
  }

  @Mutation
  SET_MATCH_HISTORY(matchHistories: MemberMatchHistoryPaginatedResponse) {
    this.matchHistoryHasMorePage = matchHistories.hasMorePage
    this.matchHistory = [...this.matchHistory, ...matchHistories.items]
  }

  @Mutation
  RESET_MATCH_HISTORY() {
    this.matchHistory = []
    this.matchHistoryCurrentPage = 0
    this.matchHistoryHasMorePage = true
  }

  // Member Tournament History
  @Action({ rawError: true })
  async fetchMemberTournamentHistory(params: {
    page: number
    game: TournamentGameType
  }) {
    let page = params.page
    let game = params.game
    if (!this.memberId) return
    if (
      page <= this.tournamentHistoryCurrentPage ||
      !this.tournamentHistoryHasMorePage
    ) {
      return
    }
    this.context.commit('SET_TOURNAMENT_HISTORY_CURRENT_PAGE', page)
    try {
      const response = await TournamentAPI.getMemberTournamentHistory(
        this.memberId,
        page,
        game
      )
      let payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.context.commit('SET_TOURNAMENT_HISTORY', payload)
      }
    } catch {}
  }

  @Action
  resetMemberTournamentHistory() {
    this.context.commit('RESET_TOURNAMENT_HISTORY')
  }

  @Mutation
  SET_TOURNAMENT_HISTORY_CURRENT_PAGE(page: number) {
    this.tournamentHistoryCurrentPage = page
  }

  @Mutation
  SET_TOURNAMENT_HISTORY(
    tournamentHistories: MemberTournamentHistoryPaginatedResponse
  ) {
    this.tournamentHistoryHasMorePage = tournamentHistories.hasMorePage
    this.tournamentHistory = [
      ...this.tournamentHistory,
      ...tournamentHistories.items
    ]
  }

  @Mutation
  RESET_TOURNAMENT_HISTORY() {
    this.tournamentHistory = []
    this.tournamentHistoryCurrentPage = 0
    this.tournamentHistoryHasMorePage = true
  }

  get authedMemberId(): string {
    return this.context.rootGetters['user/memberId'] || ''
  }

  get isProfileOwner() {
    if (!this.authedMemberId) return false
    return this.authedMemberId === this.memberId
  }

  // Phone verification
  @Mutation
  SET_IS_PHONE_VERIFICATION_VISIBLE(isVisible = false) {
    this.isPhoneVerificationVisible = isVisible
  }

  @Action({ rawError: true })
  setIsPhoneVerificationVisible(params: { isVisible: boolean }) {
    this.context.commit('SET_IS_PHONE_VERIFICATION_VISIBLE', params.isVisible)
  }

  // Discord
  @Mutation
  SET_IS_DISCORD_ID_DIALOG_VISIBLE(isVisible = false) {
    this.isDiscordIdDialogVisible = isVisible
  }

  @Action({ rawError: true })
  setIsDiscordIdDialogVisible(params: { isVisible: boolean }) {
    this.context.commit('SET_IS_DISCORD_ID_DIALOG_VISIBLE', params.isVisible)
  }
}
